import { Component, Input, OnChanges, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MODAL_SITE_COLUMNS, MODAL_SQL_COLUMNS } from '../../../../constants/constants';
import { SUMMARY_CARD_TOOLTIPS } from '../../../../constants/tooltips';
import {
  IGridKeydownEventArgs,
  IGridRowEventArgs,
  IgxGridModule,
  ISortingStrategy,
} from '@infragistics/igniteui-angular';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CustomersData } from '../../models/customers.model';
import { ColumnData, SqlDbStatus } from '../../models/models';
import { SHARED_MATERIAL_IMPORTS, SHARED_COMPONENT_IMPORTS } from '../../shared-imports';
import { HelperUtilities } from '../../utilities/helper-utilities';
import { GridColumnStrategies } from '../../utilities/grid-column-strategies';

const ROW_ACTIVATION_KEYS = ['enter', ' ', 'space', 'spacebar'];

@Component({
  selector: 'app-card-modal',
  standalone: true,
  imports: [IgxGridModule, ...SHARED_MATERIAL_IMPORTS, ...SHARED_COMPONENT_IMPORTS],
  templateUrl: './card-modal.component.html',
  styleUrls: ['./card-modal.component.scss'],
})
export class CardModalComponent implements OnChanges {
  @Input() title!: string;
  @Input() subtitle!: string;
  @Input() value!: number;
  @Input() trend?: string;
  @Input() src!: string;
  @Input() modalTemplate!: string;
  @Input() isDisabled!: boolean;
  @Input() data!: CustomersData[];

  public modalSiteColumns: ColumnData[] = MODAL_SITE_COLUMNS;
  public modalSqlColumns: ColumnData[] = MODAL_SQL_COLUMNS;

  private dialog = inject(MatDialog);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);

  public customersData: CustomersData[] = [];
  @ViewChild('totalSites') totalSitesTemplate!: TemplateRef<any>;
  @ViewChild('activeWorkstation') activeWorkstationTemplate!: TemplateRef<any>;
  @ViewChild('backupCompliance') backupComplianceTemplate!: TemplateRef<any>;
  @ViewChild('systemAlerts') systemAlertsTemplate!: TemplateRef<any>;
  @ViewChild('sqlLimits') sqlLimitsTemplate!: TemplateRef<any>;

  private templateMap: { [key: string]: TemplateRef<any> } = {};

  public ngAfterViewInit(): void {
    this.templateMap = {
      totalSites: this.totalSitesTemplate,
      activeWorkstation: this.activeWorkstationTemplate,
      backupCompliance: this.backupComplianceTemplate,
      systemAlerts: this.systemAlertsTemplate,
      sqlLimits: this.sqlLimitsTemplate,
    };
  }
  public ngOnChanges(): void {
    this.customersData = this.data;
  }

  public get cardTooltip(): string | undefined {
    return SUMMARY_CARD_TOOLTIPS[this.modalTemplate];
  }

  public sortStrategyFor(column: ColumnData): ISortingStrategy {
    return GridColumnStrategies.sorting(column);
  }

  public openCustomerDetails(selectedRow: IGridRowEventArgs): void {
    this.navigateToCustomer(selectedRow?.row?.data);
  }

  public onGridKeydown(args: IGridKeydownEventArgs): void {
    if (args.targetType !== 'dataCell') {
      return;
    }

    const key: string = ((args.event as KeyboardEvent).key ?? '').toLowerCase();
    if (!ROW_ACTIVATION_KEYS.includes(key)) {
      return;
    }

    args.event.preventDefault();
    args.cancel = true;
    this.navigateToCustomer(args.target?.row?.data);
  }

  private navigateToCustomer(rowData?: CustomersData): void {
    const systemId: string = (rowData?.systemId ?? '').toString().toUpperCase();

    if (!systemId) {
      this.snackBar.open('No data available for this customer', 'Close', {
        duration: 1000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      });
      return;
    }

    this.closeModal();
    this.router.navigate(['/customers', systemId]);
  }

  public getDbStatus(customer: CustomersData): SqlDbStatus {
    return HelperUtilities.getDbStatus(
      {
        totalSize: HelperUtilities.toBytes(customer.dbSize),
        freeSpace: HelperUtilities.toBytes(customer.dbFreeSpace),
        usedSize: HelperUtilities.toBytes(customer.dbUsedSize),
      },
      customer.sqlProductVersionRaw,
    );
  }

  public openDialog(templateName: string): void {
    const templateRef = this.templateMap[templateName];
    if (!templateRef) {
      console.error('Template not found:', templateName);
      return;
    }
    this.dialog.open(templateRef, {
      maxWidth: '60vw',
      width: '60vw',
      maxHeight: '90vh',
    });
  }

  public closeModal(): void {
    this.dialog.closeAll();
  }
}
